// Helper functions
const isUpperCase = (char) => char === char.toUpperCase() && char !== char.toLowerCase();
const isLetter = (char) => /[a-zA-Z]/.test(char);
const isNumber = (char) => /[0-9]/.test(char);
const isSpecialChar = (char) => !isLetter(char) && !isNumber(char);

const shouldAddSpaceBefore = (current, previous, next) => {
  // Handle uppercase letters, considering acronyms
  if (isUpperCase(current) && isLetter(current)) {
    // Add space if it's the start of a new word (previous is lowercase)
    // or if it's the end of an acronym (next is lowercase)
    return !isUpperCase(previous) || (isLetter(next) && !isUpperCase(next));
  }

  // Add space before numbers, unless the previous character is also a number
  if (isNumber(current)) return !isNumber(previous);

  // Add space before letters that follow numbers
  if (isLetter(current)) return isNumber(previous);

  // Add space before special characters, unless they're at the start of the string
  if (isSpecialChar(current)) return isLetter(previous) || isNumber(previous);

  return false;
};

/**
 * Converts a camel case string into a title case string with spaces between words.
 *
 * This function takes a camel case string (e.g., "canvasSet") and returns a title case
 * string with each word separated by a space and the first letter of each word capitalized
 * (e.g., "Canvas Set"). It handles already spaced strings, numbers, acronyms, and special characters correctly.
 *
 * @param {string} input - The camel case string to be converted.
 * @returns {string} - The converted title case string with spaces between each word.
 */
const camelCaseToTitleCase = (input) => {
  if (typeof input !== "string" || !input) return ""; // Check if the input is a string

  return input
    .split("")
    .reduce((acc, char, index) => {
      const prevChar = input[index - 1] || "";
      const nextChar = input[index + 1] || "";

      // Keep existing spaces
      if (char === " ") return acc + char;

      // Add space if conditions are met
      if (index !== 0 && prevChar !== " " && shouldAddSpaceBefore(char, prevChar, nextChar)) {
        return `${acc} ${char}`;
      }

      return acc + char;
    }, "")
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize the first letter
    .trim(); // Remove any leading/trailing spaces
};

export default camelCaseToTitleCase;
